// =====================================================
// PRICING ENGINE SERVICE
// Stage 2: Participants & Pricing
// Core logic for evaluating pricing rules and calculating prices
// =====================================================

import type {
  PricingRule,
  PricingCondition,
  ParticipantData,
  ParticipantType,
  PriceCalculationResult,
  PriceCalculationContext,
  RuleConflict,
  RuleValidationError,
  RuleValidationResult,
} from '@/types/pricing.types';

// ===================
// AUTHORITY ROLES
// ===================

const ROLE_CODE_BY_POSITION: Record<string, Record<string, string>> = {
  DISTRICT: {
    SUPERINTENDENT: 'SUPERINTENDENTE',
    VICE_SUPERINTENDENT: 'VICE_SUPERINTENDENTE',
    SECRETARY: 'SECRETARIO_DISTRITO',
    TREASURER: 'TESORERO_DISTRITO',
    VOCAL_1: 'VOCAL1_DISTRITO',
    VOCAL_2: 'VOCAL2_DISTRITO',
  },
  REGION: {
    PRESBYTER: 'PRESBITERO',
    VICE_PRESBYTER: 'VICE_PRESBITERO',
    SECRETARY: 'SECRETARIO_REGION',
    TREASURER: 'TESORERO_REGION',
    VOCAL_1: 'VOCAL1_REGION',
    VOCAL_2: 'VOCAL2_REGION',
  },
  NATIONAL: {
    PRESIDENT: 'PRESIDENTE_NACIONAL',
    VICE_PRESIDENT: 'VICE_PRESIDENTE_NACIONAL',
    SECRETARY: 'SECRETARIO_NACIONAL',
    TREASURER: 'TESORERO_NACIONAL',
    VOCAL_1: 'VOCAL1_NACIONAL',
    VOCAL_2: 'VOCAL2_NACIONAL',
  },
};

/** authority_positions (org_type, role) → ROLE condition code used by pricing rules */
export function mapAuthorityPositionToRoleCode(orgType: string, role: string): string | undefined {
  return ROLE_CODE_BY_POSITION[orgType]?.[role];
}

// ===================
// CONDITION EVALUATION
// ===================

const DAY_MS = 24 * 60 * 60 * 1000;

const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());

/** Whole calendar days from `from` to `to` (DST/time-of-day safe) */
function daysBetween(from: Date, to: Date): number {
  return Math.round((startOfDay(to).getTime() - startOfDay(from).getTime()) / DAY_MS);
}

/** 'YYYY-MM-DD' → local Date (null when malformed) */
export function parseDateValue(value: unknown): Date | null {
  if (typeof value !== 'string') return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : null;
}

/** Age in whole years at `ref` */
export function ageAt(birth: Date, ref: Date): number {
  let age = ref.getFullYear() - birth.getFullYear();
  const monthDiff = ref.getMonth() - birth.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && ref.getDate() < birth.getDate())) age--;
  return age;
}

/** Numbers saved as text ("30") by older versions of the form still work */
function toNumber(value: unknown): number {
  if (typeof value === 'number') return value;
  if (typeof value === 'string' && value.trim() !== '') return Number(value);
  return NaN;
}

function deriveParticipantType(data: ParticipantData): ParticipantType | undefined {
  if (data.participant_type) return data.participant_type;
  if (data.minister_id) return 'MINISTER';
  if (data.guest_id) return 'GUEST';
  return undefined;
}

/**
 * Evaluates a single condition against participant data
 */
function evaluateCondition(
  condition: PricingCondition,
  participantData: ParticipantData,
  context: PriceCalculationContext
): boolean {
  const { type, operator } = condition;

  // Get the actual value to compare based on condition type
  let actualValue: string | number | undefined;

  switch (type) {
    case 'MINISTER_DEGREE':
      actualValue = participantData.minister_degree;
      break;
    case 'MINISTER_AGE':
      // Age at the event start, so the price doesn't change when recalculated later
      if (participantData.minister_birth_date) {
        actualValue = ageAt(participantData.minister_birth_date, participantData.event_start_date);
      }
      break;
    case 'DISTRICT':
      actualValue = participantData.minister_district;
      break;
    case 'REGION':
      actualValue = participantData.minister_region;
      break;
    case 'ROLE': {
      // A minister can hold several roles (authority positions + free-text position)
      const roles = [
        ...(participantData.minister_roles ?? []),
        ...(participantData.minister_role ? [participantData.minister_role.trim().toUpperCase()] : []),
      ];
      const wanted = (Array.isArray(condition.value) ? condition.value : [condition.value]).map(String);
      const overlap = roles.some((r) => wanted.includes(r));
      switch (operator) {
        case 'EQUALS':
        case 'IN':
          return overlap;
        case 'NOT_EQUALS':
        case 'NOT_IN':
          return !overlap;
        default:
          return false;
      }
    }
    case 'PARTICIPANT_TYPE':
      actualValue = deriveParticipantType(participantData);
      break;
    case 'GUEST_TYPE':
      actualValue = participantData.guest_type;
      break;
    case 'REGISTERED_BEFORE': {
      const limit = parseDateValue(condition.value);
      if (!limit) return false;
      return startOfDay(participantData.registration_date).getTime() <= limit.getTime();
    }
    case 'EARLY_BIRD':
      // Legacy: days between registration and event start
      actualValue = daysBetween(participantData.registration_date, participantData.event_start_date);
      break;
    case 'PARTICIPANT_COUNT':
      actualValue = context.current_participant_count ?? 0;
      break;
    default:
      return false;
  }

  // If we don't have a value to compare, condition fails
  if (actualValue === undefined || actualValue === null) {
    return false;
  }

  // Numeric subjects compare numbers; a non-numeric target never matches
  let target: unknown = condition.value;
  if (typeof actualValue === 'number') {
    target = Array.isArray(condition.value) ? condition.value.map(toNumber) : toNumber(condition.value);
    if (Array.isArray(target) ? target.some(Number.isNaN) : Number.isNaN(target)) return false;
  }

  switch (operator) {
    case 'EQUALS':
      return actualValue === target;

    case 'NOT_EQUALS':
      return actualValue !== target;

    case 'IN':
      return Array.isArray(target) && (target as unknown[]).includes(actualValue);

    case 'NOT_IN':
      return Array.isArray(target) && !(target as unknown[]).includes(actualValue);

    case 'GREATER_THAN':
      return typeof actualValue === 'number' && typeof target === 'number' && actualValue > target;

    case 'LESS_THAN':
      return typeof actualValue === 'number' && typeof target === 'number' && actualValue < target;

    case 'GREATER_THAN_OR_EQUAL':
      return typeof actualValue === 'number' && typeof target === 'number' && actualValue >= target;

    case 'LESS_THAN_OR_EQUAL':
      return typeof actualValue === 'number' && typeof target === 'number' && actualValue <= target;

    case 'BETWEEN':
      return (
        typeof actualValue === 'number' &&
        Array.isArray(target) &&
        target.length === 2 &&
        actualValue >= (target[0] as number) &&
        actualValue <= (target[1] as number)
      );

    case 'CONTAINS':
      return (
        typeof actualValue === 'string' &&
        typeof target === 'string' &&
        actualValue.toLowerCase().includes(target.toLowerCase())
      );

    case 'STARTS_WITH':
      return (
        typeof actualValue === 'string' &&
        typeof target === 'string' &&
        actualValue.toLowerCase().startsWith(target.toLowerCase())
      );

    case 'ENDS_WITH':
      return (
        typeof actualValue === 'string' &&
        typeof target === 'string' &&
        actualValue.toLowerCase().endsWith(target.toLowerCase())
      );

    default:
      return false;
  }
}

/**
 * `conditions` is jsonb: some rows come back as a JSON string (double-encoded)
 * or null. Returns the array, [] for null, or null when it can't be read.
 */
export function normalizeConditions(raw: unknown): PricingCondition[] | null {
  if (raw === null || raw === undefined) return [];
  let value = raw;
  if (typeof value === 'string') {
    try {
      value = JSON.parse(value);
    } catch {
      return null;
    }
  }
  return Array.isArray(value) ? (value as PricingCondition[]) : null;
}

/**
 * Evaluates all conditions in a rule (AND logic - all must pass)
 */
function evaluateRuleConditions(
  rule: PricingRule,
  participantData: ParticipantData,
  context: PriceCalculationContext
): boolean {
  const conditions = normalizeConditions(rule.conditions);

  // Unreadable conditions: don't guess, the rule doesn't apply
  if (!conditions) return false;

  // Empty conditions array = rule always matches
  if (conditions.length === 0) return true;

  // All conditions must be true (AND logic)
  return conditions.every((condition) =>
    evaluateCondition(condition, participantData, context)
  );
}

// ===================
// PRICE CALCULATION
// ===================

/**
 * Applies a pricing rule action to calculate final price
 */
function applyPricingAction(
  basePrice: number,
  rule: PricingRule
): { finalPrice: number; discountAmount: number } {
  let finalPrice = basePrice;
  let discountAmount = 0;

  switch (rule.action_type) {
    case 'PERCENTAGE_DISCOUNT':
      discountAmount = (basePrice * rule.action_value) / 100;
      finalPrice = basePrice - discountAmount;
      break;

    case 'FIXED_DISCOUNT':
      discountAmount = rule.action_value;
      finalPrice = Math.max(0, basePrice - discountAmount); // Don't go below 0
      break;

    case 'SET_PRICE':
      finalPrice = rule.action_value;
      discountAmount = basePrice - finalPrice;
      break;
  }

  // Ensure final price is not negative
  finalPrice = Math.max(0, finalPrice);
  discountAmount = Math.max(0, discountAmount);

  return { finalPrice, discountAmount };
}

/**
 * Main pricing calculation function
 * Evaluates rules in priority order and applies the first matching rule
 */
export function calculatePrice(
  context: PriceCalculationContext,
  rules: PricingRule[]
): PriceCalculationResult {
  const { base_price, participant_data } = context;

  // Filter and sort rules: active only, sorted by priority (lower = higher priority)
  const activeRules = rules
    .filter((rule) => rule.is_active && rule.event_id === context.event_id)
    .sort((a, b) => a.priority - b.priority);

  // Find first matching rule
  let appliedRule: PricingRule | null = null;
  for (const rule of activeRules) {
    if (evaluateRuleConditions(rule, participant_data, context)) {
      appliedRule = rule;
      break; // Stop at first match
    }
  }

  // Calculate final price
  let finalPrice = base_price;
  let discountAmount = 0;
  let calculationDetails = '';

  if (appliedRule) {
    const result = applyPricingAction(base_price, appliedRule);
    finalPrice = result.finalPrice;
    discountAmount = result.discountAmount;

    // Build explanation
    calculationDetails = buildCalculationDetails(base_price, appliedRule, finalPrice, discountAmount);
  } else {
    calculationDetails = `Precio base aplicado (sin descuentos): Bs ${base_price.toFixed(2)}`;
  }

  const discountPercentage = base_price > 0 ? (discountAmount / base_price) * 100 : 0;

  return {
    base_price,
    final_price: Math.round(finalPrice * 100) / 100, // Round to 2 decimals
    discount_amount: Math.round(discountAmount * 100) / 100,
    discount_percentage: Math.round(discountPercentage * 100) / 100,
    applied_rule: appliedRule,
    calculation_details: calculationDetails,
  };
}

/**
 * Builds human-readable calculation details
 */
function buildCalculationDetails(
  basePrice: number,
  rule: PricingRule,
  finalPrice: number,
  discountAmount: number
): string {
  const lines: string[] = [];

  lines.push(`Regla aplicada: ${rule.name}`);
  if (rule.description) {
    lines.push(`Descripción: ${rule.description}`);
  }

  lines.push(`Precio base: Bs ${basePrice.toFixed(2)}`);

  switch (rule.action_type) {
    case 'PERCENTAGE_DISCOUNT':
      lines.push(`Descuento: ${rule.action_value}% (-Bs ${discountAmount.toFixed(2)})`);
      break;
    case 'FIXED_DISCOUNT':
      lines.push(`Descuento fijo: -Bs ${discountAmount.toFixed(2)}`);
      break;
    case 'SET_PRICE':
      lines.push(`Precio especial establecido: Bs ${finalPrice.toFixed(2)}`);
      break;
  }

  lines.push(`Precio final: Bs ${finalPrice.toFixed(2)}`);

  return lines.join('\n');
}

// ===================
// RULE VALIDATION
// ===================

/**
 * Validates a pricing rule for common issues
 */
export function validatePricingRule(rule: Partial<PricingRule>): RuleValidationResult {
  const errors: RuleValidationError[] = [];
  const warnings: RuleValidationError[] = [];

  // Required fields
  if (!rule.name || rule.name.trim().length === 0) {
    errors.push({
      field: 'name',
      message: 'El nombre de la regla es obligatorio',
      severity: 'error',
    });
  }

  if (!rule.event_id) {
    errors.push({
      field: 'event_id',
      message: 'El evento es obligatorio',
      severity: 'error',
    });
  }

  if (!rule.action_type) {
    errors.push({
      field: 'action_type',
      message: 'El tipo de acción es obligatorio',
      severity: 'error',
    });
  }

  if (rule.action_value === undefined || rule.action_value === null) {
    errors.push({
      field: 'action_value',
      message: 'El valor de la acción es obligatorio',
      severity: 'error',
    });
  }

  // Validate action value ranges
  if (rule.action_type === 'PERCENTAGE_DISCOUNT') {
    if (rule.action_value !== undefined && (rule.action_value < 0 || rule.action_value > 100)) {
      errors.push({
        field: 'action_value',
        message: 'El porcentaje de descuento debe estar entre 0 y 100',
        severity: 'error',
      });
    }
  }

  if (
    (rule.action_type === 'FIXED_DISCOUNT' || rule.action_type === 'SET_PRICE') &&
    rule.action_value !== undefined &&
    rule.action_value < 0
  ) {
    errors.push({
      field: 'action_value',
      message: 'El valor no puede ser negativo',
      severity: 'error',
    });
  }

  // Validate conditions
  if (rule.conditions && rule.conditions.length === 0) {
    warnings.push({
      field: 'conditions',
      message: 'La regla no tiene condiciones y se aplicará a todos los participantes',
      severity: 'warning',
    });
  }

  // Validate priority
  if (rule.priority !== undefined && rule.priority < 0) {
    errors.push({
      field: 'priority',
      message: 'La prioridad no puede ser negativa',
      severity: 'error',
    });
  }

  return {
    is_valid: errors.length === 0,
    errors,
    warnings,
  };
}

/**
 * Detects rules that can never apply or whose order is ambiguous.
 * Only inactive-free, same-event rules are compared, in evaluation order.
 */
export function detectRuleConflicts(rules: PricingRule[]): RuleConflict[] {
  const conflicts: RuleConflict[] = [];
  const ordered = rules.filter((r) => r.is_active).sort((a, b) => a.priority - b.priority);

  for (let i = 0; i < ordered.length; i++) {
    for (let j = i + 1; j < ordered.length; j++) {
      const earlier = ordered[i];
      const later = ordered[j];

      // Different events = no conflict
      if (earlier.event_id !== later.event_id) continue;

      if (earlier.priority === later.priority) {
        conflicts.push({
          rule1: earlier,
          rule2: later,
          conflict_type: 'SAME_PRIORITY',
          description: 'Tienen la misma prioridad: el orden entre ambas es ambiguo.',
          severity: 'high',
        });
      } else if (isShadowedBy(later, earlier)) {
        conflicts.push({
          rule1: earlier,
          rule2: later,
          conflict_type: 'SHADOWED_RULE',
          description: `«${later.name}» nunca se aplicará: «${earlier.name}» (que va antes) ya cubre a todos sus participantes.`,
          severity: 'high',
        });
      }
    }
  }

  return conflicts;
}

/** `later` never applies when every participant matching it also matches `earlier` */
function isShadowedBy(later: PricingRule, earlier: PricingRule): boolean {
  return earlier.conditions.every((ce) => later.conditions.some((cl) => implies(cl, ce)));
}

type Interval = [number, number];

function toInterval(c: PricingCondition): Interval | null {
  const nums = (Array.isArray(c.value) ? c.value : [c.value]).map(toNumber);
  if (nums.some(Number.isNaN)) return null;
  const [a, b] = nums;
  switch (c.operator) {
    case 'EQUALS':
      return [a, a];
    case 'GREATER_THAN':
      return [a + 1, Infinity];
    case 'GREATER_THAN_OR_EQUAL':
      return [a, Infinity];
    case 'LESS_THAN':
      return [-Infinity, a - 1];
    case 'LESS_THAN_OR_EQUAL':
      return [-Infinity, a];
    case 'BETWEEN':
      return nums.length === 2 ? [a, b] : null;
    default:
      return null;
  }
}

function toSet(c: PricingCondition): { negative: boolean; values: Set<string> } | null {
  const values = new Set((Array.isArray(c.value) ? c.value : [c.value]).map(String));
  switch (c.operator) {
    case 'EQUALS':
    case 'IN':
      return { negative: false, values };
    case 'NOT_EQUALS':
    case 'NOT_IN':
      return { negative: true, values };
    default:
      return null;
  }
}

/** Does every participant matching `a` also match `b`? (conservative: false when unsure) */
function implies(a: PricingCondition, b: PricingCondition): boolean {
  if (a.type !== b.type) return false;

  if (a.type === 'REGISTERED_BEFORE') {
    const da = parseDateValue(a.value);
    const db = parseDateValue(b.value);
    return !!da && !!db && da.getTime() <= db.getTime();
  }

  const ia = toInterval(a);
  const ib = toInterval(b);
  if (ia && ib) return ia[0] >= ib[0] && ia[1] <= ib[1];

  const sa = toSet(a);
  const sb = toSet(b);
  if (sa && sb) {
    const subset = (x: Set<string>, y: Set<string>) => [...x].every((v) => y.has(v));
    if (!sa.negative && !sb.negative) return subset(sa.values, sb.values);
    if (sa.negative && sb.negative) return subset(sb.values, sa.values);
    if (!sa.negative && sb.negative) return ![...sa.values].some((v) => sb.values.has(v));
    return false;
  }

  return JSON.stringify([a.operator, a.value]) === JSON.stringify([b.operator, b.value]);
}

// ===================
// BATCH CALCULATIONS
// ===================

/**
 * Calculate prices for multiple participants at once
 */
export function batchCalculatePrices(
  contexts: PriceCalculationContext[],
  rules: PricingRule[]
): PriceCalculationResult[] {
  return contexts.map((context) => calculatePrice(context, rules));
}
